import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck, ChevronDown, Info } from 'lucide-react';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Icon } from '@/components/ui/icon.js';
import { ApiError, mapApiError, type Translator } from '@/lib/api/error.js';
import { getActiveSeed, rotateSeed, verifyRound } from './api.js';
import { useGuestGate } from './useGuestGate.js';
import type { VerifyResult } from './types.js';

interface Props {
  /** Changes per round so the active seed (nonce) refetches after each play. */
  refreshKey?: string;
  /** Most recent round to verify; verify button is hidden when absent. */
  roundId?: string;
}

const ACTIVE_SEED_KEY = (refreshKey?: string) =>
  ['originals', 'seed', 'active', refreshKey ?? 'init'] as const;

export type VerifyVerdict = 'confirming' | 'in-play' | 'unverifiable' | 'verified' | 'mismatch';

/**
 * Single-shot rounds verify on `match` AND must not contradict `presentationMatch`
 * (the server's own replay of the visual result from the seed). Mines rounds carry
 * `minePositionsMatch` instead of `match` — judging them on `match` would render a
 * fair round as a fraud signal.
 */
export function verifyVerdict(v: VerifyResult): VerifyVerdict {
  if (v.confirming === true) return 'confirming';
  if (v.inPlay === true) return 'in-play';
  if (v.verifiable === false) return 'unverifiable';
  if (v.gameCode === 'mines') return v.minePositionsMatch === true ? 'verified' : 'mismatch';
  return v.match === true && v.presentationMatch !== false ? 'verified' : 'mismatch';
}

/** Why a round cannot be verified yet: waiting on its stake, a board still in play, or a seed still secret. */
const UNVERIFIED_COPY = {
  confirming: 'fairness.confirming',
  'in-play': 'fairness.inPlay',
  unverifiable: 'fairness.unverifiable',
} as const;

/** The server refuses to reveal a seed while a round drawn from it is unresolved. */
const ROTATE_IN_PLAY_CODE = 'ORIG-006';

/**
 * What a refused rotation tells the player. A round still being played or
 * confirmed holds the seed (its outcome would be readable once the seed is
 * revealed), so the refusal says to wait for the round, not that something broke.
 */
export function rotateErrorMessage(err: unknown, t: Translator): string {
  if (err instanceof ApiError && err.code === ROTATE_IN_PLAY_CODE) return t('fairness.rotateWhileInPlay');
  return mapApiError(err, t);
}

function SeedRow({
  label,
  hint,
  value,
  copied,
  onCopy,
}: {
  label: string;
  hint: string;
  value: string;
  copied?: boolean;
  onCopy?: () => void;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] p-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">{label}</span>
        {onCopy && (
          <button
            type="button"
            onClick={onCopy}
            aria-label={label}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[var(--bg-sunken)] text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <Icon name={copied ? 'check' : 'copy'} size={14} className={copied ? 'text-[var(--success)]' : ''} />
          </button>
        )}
      </div>
      <span className="break-all font-mono text-[13px] leading-snug text-[var(--text-primary)]" title={value}>
        {value}
      </span>
      <span className="text-[11px] leading-relaxed text-[var(--text-muted)]">{hint}</span>
    </div>
  );
}

export function ProvablyFairPanel({ refreshKey, roundId }: Props) {
  const { t } = useTranslation('originals');
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  // Seeds and rounds belong to a member; a visitor reads how fairness works and is
  // offered a sign-in instead.
  const guest = useGuestGate();

  const seedQuery = useQuery({
    queryKey: ACTIVE_SEED_KEY(refreshKey),
    queryFn: getActiveSeed,
    enabled: open && !guest.isGuest,
    staleTime: 0,
  });

  const rotateMutation = useMutation({
    mutationFn: () => rotateSeed(),
    onSuccess: () => {
      toast.success(t('fairness.rotated'));
      setVerify(null);
      void qc.invalidateQueries({ queryKey: ['originals', 'seed', 'active'] });
    },
    onError: (err) => {
      toast.error(rotateErrorMessage(err, t));
      // A rotation that lost to another one (ORIG-024) leaves a new seed live.
      void qc.invalidateQueries({ queryKey: ['originals', 'seed', 'active'] });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (id: string) => verifyRound(id),
    onSuccess: (res) => setVerify(res),
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  function handleCopy(key: string, value: string) {
    void navigator.clipboard.writeText(value).catch(() => null);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
  }

  const seed = seedQuery.data;

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--primary)]/15 text-[var(--primary)]">
          <ShieldCheck size={18} aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[14px] font-bold text-[var(--text-primary)]">{t('fairness.title')}</span>
          <span className="truncate text-[12px] text-[var(--text-muted)]">{t('guide.fair.title')}</span>
        </span>
        <ChevronDown
          size={18}
          className={['shrink-0 text-[var(--text-muted)] transition-transform', open ? 'rotate-180' : ''].join(' ')}
          aria-hidden
        />
      </button>

      {open && (
        <div className="flex flex-col gap-4 border-t border-[var(--border-subtle)] px-4 pb-4 pt-4">
          <p className="text-[12.5px] leading-relaxed text-[var(--text-secondary)]">{t('guide.fair.intro')}</p>

          {guest.isGuest ? (
            <div className="flex flex-col items-start gap-2.5 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] p-3.5">
              <span className="text-[12.5px] leading-relaxed text-[var(--text-secondary)]">{t('guest.fairnessNote')}</span>
              <Button variant="primary" size="md" onClick={guest.signIn}>
                {t('auth.signIn', { ns: 'common' })}
              </Button>
            </div>
          ) : seedQuery.isPending ? (
            <div className="h-40 animate-pulse rounded-xl bg-[var(--bg-elevated)]/50" />
          ) : seedQuery.isError || !seed ? (
            <div className="py-2 text-[12px] text-[var(--danger)]">{t('errorLoading')}</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              <SeedRow
                label={t('fairness.serverSeedHash')}
                hint={t('guide.fair.serverSeed')}
                value={seed.serverSeedHash}
                copied={copiedKey === 'server'}
                onCopy={() => handleCopy('server', seed.serverSeedHash)}
              />
              {seed.nextServerSeedHash && (
                <SeedRow
                  label={t('fairness.nextServerSeedHash')}
                  hint={t('guide.fair.nextServerSeed')}
                  value={seed.nextServerSeedHash}
                  copied={copiedKey === 'next'}
                  onCopy={() => handleCopy('next', seed.nextServerSeedHash!)}
                />
              )}
              <SeedRow
                label={t('fairness.clientSeed')}
                hint={t('guide.fair.clientSeed')}
                value={seed.clientSeed}
                copied={copiedKey === 'client'}
                onCopy={() => handleCopy('client', seed.clientSeed)}
              />
              <SeedRow
                label={t('fairness.nonce')}
                hint={t('guide.fair.nonce')}
                value={String(seed.nonce)}
              />
            </div>
          )}

          {!guest.isGuest && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="md"
                disabled={rotateMutation.isPending}
                onClick={() => rotateMutation.mutate()}
              >
                <Icon name="rotate" size={15} />
                {t('fairness.rotate')}
              </Button>
              {roundId && (
                <Button
                  variant="primary"
                  size="md"
                  disabled={verifyMutation.isPending}
                  onClick={() => verifyMutation.mutate(roundId)}
                >
                  <Icon name="check" size={15} />
                  {t('fairness.verify')}
                </Button>
              )}
            </div>
          )}

          {verify &&
            (['confirming', 'in-play', 'unverifiable'].includes(verifyVerdict(verify)) ? (
              <div className="flex items-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3.5 py-3 text-[12.5px] text-[var(--text-secondary)]">
                <Info size={16} className="shrink-0 text-[var(--text-muted)]" />
                {t(UNVERIFIED_COPY[verifyVerdict(verify) as 'confirming' | 'in-play' | 'unverifiable'])}
              </div>
            ) : verifyVerdict(verify) === 'verified' ? (
              <div className="flex items-center gap-2 rounded-xl border border-[var(--success)]/40 bg-[var(--success-bg)] px-3.5 py-3 text-[12.5px] font-semibold text-[var(--success)]">
                <Icon name="check" size={16} className="shrink-0" />
                {t('fairness.verified')}
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-xl border border-[var(--danger)]/40 bg-[var(--danger-bg)] px-3.5 py-3 text-[12.5px] font-semibold text-[var(--danger)]">
                <Icon name="close" size={16} className="shrink-0" />
                {t('fairness.mismatch')}
              </div>
            ))}
        </div>
      )}
    </Card>
  );
}
